import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Meta, Title } from '@angular/platform-browser';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';

interface RegistrationResponse {
  doc?: {
    id: string;
    importoDovuto: number;
  };
  message?: string;
}

@Component({
  selector: 'app-gita-missionaria-napoli',
  standalone: true,
  templateUrl: './gita-missionaria-napoli.component.html',
  styleUrl: './gita-missionaria-napoli.component.scss',
  imports: [CommonModule, FormsModule],
})
export class GitaMissionariaNapoliComponent implements OnInit {
  private http = inject(HttpClient);
  private meta = inject(Meta);
  private title = inject(Title);

  // Dati anagrafici
  nome: string = '';
  cognome: string = '';
  telefono: string = '';
  dataNascita: string = '';

  // Minorenne (calcolati automaticamente)
  tipoAccompagnamento: string = '';
  nomeGenitore: string = '';
  cognomeGenitore: string = '';
  delegaFile: File | null = null;
  delegaId: string = '';
  fileError: string = '';

  // Data evento per calcolo età
  private readonly DATA_EVENTO = new Date('2026-03-19');

  // Trasporto
  tipoTrasporto: string = '';

  // Note
  note: string = '';

  // Privacy
  accettazionePrivacy: boolean = false;
  showPrivacyDialog: boolean = false;

  // UI state
  isSubmitting: boolean = false;
  isSuccess: boolean = false;
  successMessage: string = '';
  errorMessage: string = '';
  importoDovuto: number = 0;

  // Prezzi
  readonly PREZZO_PULLMAN_ADULTO = 170;
  readonly PREZZO_PULLMAN_BAMBINO = 140;
  readonly PREZZO_MEZZI_PROPRI_ADULTO = 70;
  readonly PREZZO_MEZZI_PROPRI_BAMBINO = 35;
  readonly PREZZO_SOLO_PASTI_ADULTO = 40;
  readonly PREZZO_SOLO_PASTI_BAMBINO = 30;

  ngOnInit() {
    this.title.setTitle(
      'Gita Missionaria Napoli - 19-22 Marzo 2026 | Come Tralci',
    );

    this.meta.updateTag({
      name: 'description',
      content:
        'Iscriviti alla Gita Missionaria a Napoli dal 19 al 22 marzo 2026. Visita alla città e incontri con le chiese della Campania.',
    });
    this.meta.updateTag({
      name: 'keywords',
      content:
        'Gita Missionaria, Napoli, Come Tralci, missioni, chiesa, marzo 2026',
    });

    // Open Graph tags
    this.meta.updateTag({
      property: 'og:title',
      content: 'Gita Missionaria Napoli - Marzo 2026',
    });
    this.meta.updateTag({
      property: 'og:description',
      content:
        'Iscriviti alla Gita Missionaria a Napoli dal 19 al 22 marzo 2026.',
    });
    this.meta.updateTag({ property: 'og:type', content: 'event' });
    this.meta.updateTag({
      property: 'og:url',
      content: 'https://cometralci.com/eventi/2026/gita-missionaria-napoli',
    });
    this.meta.updateTag({ property: 'og:site_name', content: 'Come Tralci' });
    this.meta.updateTag({ property: 'og:locale', content: 'it_IT' });

    // Twitter Card
    this.meta.updateTag({
      name: 'twitter:card',
      content: 'summary_large_image',
    });
    this.meta.updateTag({
      name: 'twitter:title',
      content: 'Gita Missionaria Napoli',
    });
    this.meta.updateTag({
      name: 'twitter:description',
      content: 'Iscrizione alla gita missionaria. 19-22 Marzo 2026.',
    });

    // Canonical URL
    this.meta.updateTag({
      rel: 'canonical',
      href: 'https://cometralci.com/eventi/2026/gita-missionaria-napoli',
    });
  }

  /**
   * Calcola l'età alla data dell'evento
   */
  get etaAllaDataEvento(): number | null {
    if (!this.dataNascita) return null;

    const nascita = new Date(this.dataNascita);
    if (isNaN(nascita.getTime())) return null;

    let eta = this.DATA_EVENTO.getFullYear() - nascita.getFullYear();
    const meseEvento = this.DATA_EVENTO.getMonth();
    const giornoEvento = this.DATA_EVENTO.getDate();
    const meseNascita = nascita.getMonth();
    const giornoNascita = nascita.getDate();

    // Se il compleanno non è ancora passato alla data dell'evento
    if (
      meseNascita > meseEvento ||
      (meseNascita === meseEvento && giornoNascita > giornoEvento)
    ) {
      eta--;
    }

    return eta;
  }

  /**
   * Verifica se è minorenne alla data dell'evento
   */
  get isMinorenne(): boolean {
    const eta = this.etaAllaDataEvento;
    return eta !== null && eta < 18;
  }

  /**
   * Verifica se è bambino (fino a 10 anni) alla data dell'evento
   */
  get isBambino(): boolean {
    const eta = this.etaAllaDataEvento;
    return eta !== null && eta <= 10;
  }

  /**
   * Calcola il prezzo in base al tipo di trasporto e se è bambino
   */
  get calcolaPrezzoTotale(): number {
    if (!this.tipoTrasporto) return 0;

    if (this.tipoTrasporto === 'pullman') {
      return this.isBambino
        ? this.PREZZO_PULLMAN_BAMBINO
        : this.PREZZO_PULLMAN_ADULTO;
    } else if (this.tipoTrasporto === 'solo-pasti') {
      return this.isBambino
        ? this.PREZZO_SOLO_PASTI_BAMBINO
        : this.PREZZO_SOLO_PASTI_ADULTO;
    } else {
      return this.isBambino
        ? this.PREZZO_MEZZI_PROPRI_BAMBINO
        : this.PREZZO_MEZZI_PROPRI_ADULTO;
    }
  }

  /**
   * Calcola l'acconto richiesto
   */
  get calcolaAcconto(): number {
    if (!this.tipoTrasporto) return 0;
    if (this.tipoTrasporto === 'pullman') return 60;
    if (this.tipoTrasporto === 'solo-pasti') return this.calcolaPrezzoTotale;
    return 20;
  }

  /**
   * Formatta il prezzo
   */
  formatPrice(amount: number): string {
    return amount + ' \u20AC';
  }

  /**
   * Verifica se il form è valido
   */
  isFormValid(): boolean {
    // Campi base obbligatori
    if (
      !this.nome ||
      !this.cognome ||
      !this.telefono ||
      !this.dataNascita ||
      !this.tipoTrasporto
    ) {
      return false;
    }

    // Verifica che la data di nascita sia valida
    if (this.etaAllaDataEvento === null) {
      return false;
    }

    // Se minorenne, validare i campi aggiuntivi
    if (this.isMinorenne) {
      if (!this.tipoAccompagnamento) {
        return false;
      }

      if (this.tipoAccompagnamento === 'accompagnato') {
        if (!this.nomeGenitore || !this.cognomeGenitore) {
          return false;
        }
      }

      if (this.tipoAccompagnamento === 'non-accompagnato') {
        if (!this.delegaFile) {
          return false;
        }
      }
    }

    return true;
  }

  /**
   * Gestisce la selezione del file
   */
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    const file = input.files[0];
    const maxSize = 10 * 1024 * 1024; // 10MB

    // Verifica dimensione
    if (file.size > maxSize) {
      this.fileError = 'Il file supera la dimensione massima di 10MB';
      this.delegaFile = null;
      return;
    }

    // Verifica tipo
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png',
    ];
    if (!allowedTypes.includes(file.type)) {
      this.fileError = 'Formato non supportato. Usa PDF, JPG o PNG';
      this.delegaFile = null;
      return;
    }

    this.fileError = '';
    this.delegaFile = file;
  }

  /**
   * Rimuove il file selezionato
   */
  removeFile(event: Event): void {
    event.stopPropagation();
    this.delegaFile = null;
    this.delegaId = '';
    this.fileError = '';
  }

  /**
   * Carica il file della delega
   */
  private async uploadDelega(): Promise<string | null> {
    if (!this.delegaFile) {
      return null;
    }

    const formData = new FormData();
    formData.append('file', this.delegaFile);
    formData.append('nomeMinore', this.nome);
    formData.append('cognomeMinore', this.cognome);

    return new Promise((resolve, reject) => {
      this.http
        .post<{ doc: { id: string } }>(
          `${environment.apiUrl}/deleghe-minori`,
          formData,
        )
        .subscribe({
          next: (response) => {
            resolve(response.doc.id);
          },
          error: (error) => {
            console.error('Errore upload delega:', error);
            reject(error);
          },
        });
    });
  }

  /**
   * Gestisce il submit del form
   */
  async onSubmit() {
    this.isSubmitting = true;
    this.successMessage = '';
    this.errorMessage = '';

    try {
      // Se minorenne non accompagnato, carica prima la delega
      let delegaId: string | null = null;
      if (
        this.isMinorenne &&
        this.tipoAccompagnamento === 'non-accompagnato' &&
        this.delegaFile
      ) {
        delegaId = await this.uploadDelega();
      }

      const registrationData: Record<string, unknown> = {
        nome: this.nome.trim(),
        cognome: this.cognome.trim(),
        telefono: this.telefono.trim(),
        dataNascita: this.dataNascita,
        tipoTrasporto: this.tipoTrasporto,
        note: this.note?.trim() || '',
        accettazionePrivacy: this.accettazionePrivacy,
      };

      // Aggiungi campi minorenne se applicabile
      if (this.isMinorenne) {
        registrationData['tipoAccompagnamento'] = this.tipoAccompagnamento;

        if (this.tipoAccompagnamento === 'accompagnato') {
          registrationData['nomeGenitore'] = this.nomeGenitore.trim();
          registrationData['cognomeGenitore'] = this.cognomeGenitore.trim();
        }

        if (
          this.tipoAccompagnamento === 'non-accompagnato' &&
          delegaId
        ) {
          registrationData['delega'] = delegaId;
        }
      }

      this.http
        .post<RegistrationResponse>(
          `${environment.apiUrl}/iscrizioni-gita-missionaria`,
          registrationData,
        )
        .subscribe({
          next: (response) => {
            this.isSubmitting = false;
            this.isSuccess = true;
            this.importoDovuto =
              response.doc?.importoDovuto || this.calcolaPrezzoTotale;
            this.successMessage = `Iscrizione completata con successo! Il tuo importo totale da versare in contanti al fratello Flavio: ${this.formatPrice(this.importoDovuto)}`;
          },
          error: (error) => {
            this.isSubmitting = false;
            console.error('Errore iscrizione:', error);
            this.errorMessage =
              error.error?.message ||
              "Si è verificato un errore durante l'iscrizione. Riprova più tardi.";
          },
        });
    } catch (error) {
      this.isSubmitting = false;
      console.error('Errore:', error);
      this.errorMessage =
        'Si è verificato un errore durante il caricamento della delega. Riprova più tardi.';
    }
  }

  /**
   * Resetta il form per una nuova iscrizione
   */
  resetForm() {
    this.nome = '';
    this.cognome = '';
    this.telefono = '';
    this.dataNascita = '';
    this.tipoAccompagnamento = '';
    this.nomeGenitore = '';
    this.cognomeGenitore = '';
    this.delegaFile = null;
    this.delegaId = '';
    this.fileError = '';
    this.tipoTrasporto = '';
    this.note = '';
    this.accettazionePrivacy = false;
    this.isSuccess = false;
    this.successMessage = '';
    this.errorMessage = '';
    this.importoDovuto = 0;
  }

  /**
   * Apre il dialog dell'informativa privacy
   */
  openPrivacyDialog() {
    this.showPrivacyDialog = true;
  }

  /**
   * Chiude il dialog dell'informativa privacy
   */
  closePrivacyDialog() {
    this.showPrivacyDialog = false;
  }
}
